import { TableColumnConfigInterface } from '../../table.interface';
import {
  BooleanMatchStrategy,
  DateMatchStrategy,
  NumberMatchStrategy,
  TextMatchStrategy,
} from './row.filter.interface';

export class RowFilter<T> {
  private _config: Record<string, TableColumnConfigInterface<T>>;

  constructor(config: TableColumnConfigInterface<T>[] | Record<string, TableColumnConfigInterface<T>>) {
    this._config = Array.isArray(config)
      ? config.reduce((acc, column) => {
          acc[column.key as string] = column;
          return acc;
        }, {})
      : config;
  }

  public static readonly comparers = {
    text: {
      [TextMatchStrategy.CONTAINS]: (rowVal, filterVal) =>
        rowVal?.toString().toLowerCase().includes(filterVal?.toString().toLowerCase()),
      [TextMatchStrategy.EXACT]: (rowVal, filterVal) =>
        rowVal?.toString().toLowerCase() === filterVal?.toString().toLowerCase(),
      [TextMatchStrategy.STARTSWITH]: (rowVal, filterVal) =>
        rowVal?.toString().toLowerCase().startsWith(filterVal?.toString().toLowerCase()),
    },
    boolean: {
      [BooleanMatchStrategy.CONVERT]: (rowVal, filterVal) => !!rowVal === !!filterVal,
    },
    number: {
      [NumberMatchStrategy.CONVERT]: (rowVal, filterVal) => rowVal === +filterVal,
    },
    date: {
      [DateMatchStrategy.CONTAINS]: (rowVal, filterVal) =>
        rowVal?.toString().toLowerCase().includes(filterVal.toString().toLowerCase()),
    },
  };

  public filter(row: T, key: string, value: unknown): boolean {
    if (value == null || value === '') return true;

    const config = this._config[key];
    const matcher = this._getMatcher(config);

    if (Array.isArray(value)) {
      if (!value.length) return true; // If value is an empty array, don't filter
      return value.some((option) => matcher(row[key], option.label));
    }

    return matcher(row[key], value);
  }

  private _getMatcher(config: TableColumnConfigInterface): (rowValue, filterValue) => boolean {
    if (!config?.type) {
      return () => true; // Don't filter if no config is found
    }

    if (config.type === 'text') {
      return (rowVal, filterVal) => this._textMatch(rowVal, filterVal, config.filterMatchStrategy as TextMatchStrategy);
    }

    if (config.type === 'boolean' || config.type === 'boolean-sync' || config.type === 'switch') {
      return (rowVal, filterVal) =>
        this._booleanMatch(rowVal, filterVal, config.filterMatchStrategy as BooleanMatchStrategy);
    }

    if (config.type === 'number') {
      return (rowVal, filterVal) =>
        this._numberMatch(rowVal, filterVal, config.filterMatchStrategy as NumberMatchStrategy);
    }

    if (config.type === 'date' || config.type === 'datetime') {
      return (rowVal, filterVal) => this._dateMatch(rowVal, filterVal, config.filterMatchStrategy as DateMatchStrategy);
    }

    return (rowVal, filterVal) => rowVal === filterVal; // Default matcher for other types
  }

  private _textMatch(rowValue: string, filterValue: string, strategy?: TextMatchStrategy): boolean {
    const defaultStrategy = TextMatchStrategy.CONTAINS;
    const comparer = RowFilter.comparers.text[strategy] || RowFilter.comparers.text[defaultStrategy];

    return comparer(rowValue, filterValue);
  }

  private _booleanMatch(rowValue: boolean, filterValue: boolean, strategy?: BooleanMatchStrategy): boolean {
    const defaultStrategy = BooleanMatchStrategy.CONVERT;
    const comparer = RowFilter.comparers.boolean[strategy] || RowFilter.comparers.boolean[defaultStrategy];

    return comparer(rowValue, filterValue);
  }

  private _numberMatch(rowValue: number, filterValue: number, strategy?: NumberMatchStrategy): boolean {
    const defaultStrategy = NumberMatchStrategy.CONVERT;
    const comparer = RowFilter.comparers.number[strategy] || RowFilter.comparers.number[defaultStrategy];

    return comparer(rowValue, filterValue);
  }

  private _dateMatch(rowValue: Date, filterValue: Date, strategy?: DateMatchStrategy): boolean {
    const defaultStrategy = DateMatchStrategy.CONTAINS;
    const comparer = RowFilter.comparers.date[strategy] || RowFilter.comparers.date[defaultStrategy];

    return comparer(rowValue, filterValue);
  }
}
